import { fetchAvailableEmployees, recalculateEmployeeAvailability } from '@/components/scheduler/fetch.svelte';
import { toast } from 'svelte-sonner';
import { SvelteDate, SvelteMap } from 'svelte/reactivity';
import { getMouseDate, getUserFromTop, roundDate } from './lib';
import type { OverlappingJobs } from './overlappingJobs.svelte';
import type { DropPreview, PageState } from './state';
import { getDragLeftDurDown, getDragLeftDurMove, getDragPosDownPos, getDragPosMovePos, getDragRightDurMove } from './svelte/drag';
import { SchedularEmployee, SchedularEmployeeAssignment, SchedularVehicleAssignment, SchedularVehicles, SiteVisitMap, View } from './types';
import type { ZoomRangeWithBlocks } from './zoom2.svelte';

/**
 * Calculates where a job would land if dropped at the given coordinates.
 * Does NOT mutate the job - returns a DropPreview instead.
 */
export const calculateDropLocation = (
    x: number,
    y: number,
    duration: number,
    isSiteVisit: boolean,
    dragTargetDiv: HTMLDivElement,
    contentDiv: HTMLDivElement,
    usersWidth: number,
    rowStepPx: number,
    view: View,
    vehicles: SchedularVehicles,
    overlappingJobs: OverlappingJobs,
): DropPreview => {
    if (dragTargetDiv === undefined) return null;

    const boundingRect = dragTargetDiv.getBoundingClientRect();
    const hoveringDate = getMouseDate(x, view.currentZoomStore, contentDiv, usersWidth);
    const roundedDate = roundDate(hoveringDate);

    const rowY = (y - boundingRect.y) / rowStepPx;
    const pos = {
        top: Math.round(rowY),
    };

    const maxY = boundingRect.height / rowStepPx;

    const firstDateMs = view.currentZoomStore.blocks[0].start.getTime();
    const offsetMs = contentDiv.scrollLeft * (view.currentZoomStore.zoomRangePreset.cellMs / view.currentZoomStore.cellPx);
    const leftDateMs = firstDateMs + offsetMs;

    const offsetRightMs =
        (contentDiv.scrollLeft + contentDiv.getBoundingClientRect().width - usersWidth) *
        (view.currentZoomStore.zoomRangePreset.cellMs / view.currentZoomStore.cellPx);

    const rightDateMs = firstDateMs + offsetRightMs;

    const roundedDateMs = roundedDate.getTime();

    // Check if drop is outside valid bounds
    if (pos.top < 0 || pos.top >= maxY || roundedDateMs + duration * 1000 < leftDateMs || roundedDateMs > rightDateMs) {
        // Site visits should not be unscheduled when dropped outside bounds
        if (isSiteVisit) {
            return null;
        }
        // Return unscheduled state
        return {
            start: null,
            end: null,
            vehicle: null,
            duration,
        };
    }

    const vehicle = getUserFromTop(vehicles, pos.top, overlappingJobs);
    const end = new SvelteDate(roundedDate.getTime() + duration * 1000);

    return {
        start: roundedDate,
        end,
        vehicle: vehicle ?? null,
        duration,
    };
};

/**
 * Applies a drop preview to the actual vehicle assignment.
 * Call this only on mouseup to commit the change.
 */
export const applyDropPreview = (vehicleAssignment: SchedularVehicleAssignment, dropPreview: DropPreview) => {
    if (dropPreview === null || dropPreview.start === null || dropPreview.end === null) return;

    vehicleAssignment.start = dropPreview.start;
    vehicleAssignment.end = dropPreview.end;
    vehicleAssignment.vehicle = dropPreview.vehicle;
    vehicleAssignment.duration = dropPreview.duration;

    // Update employee assignments times
    if (vehicleAssignment.employeeAssignments) {
        for (const assignment of vehicleAssignment.employeeAssignments.values()) {
            assignment.start_date_time = dropPreview.start;
            assignment.end_date_time = dropPreview.end;
        }
    }

    // Mark as modified so save detects changes
    vehicleAssignment.modified = true;
};

export const onMouseMoveJob = (
    e: MouseEvent,
    zoom: ZoomRangeWithBlocks,
    pageState: PageState<'job'>,
    vehicles: SchedularVehicles,
    dragTargetDiv: HTMLDivElement,
    contentDiv: HTMLDivElement,
    usersWidth: number,
    rowStepPx: number,
    view: View,
    overlappingJobs: OverlappingJobs,
    vehicleAssignmentMap: SvelteMap<string, SchedularVehicleAssignment>,
    employeesMap: SvelteMap<string, SchedularEmployee>,
    overlappingEmployeeAssignments: SvelteMap<
        string,
        { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }
    >,
    siteVisitMap?: SiteVisitMap,
) => {
    if (pageState?.selected?.dragging == null) return;
    if (pageState.selected.type !== 'job') throw new Error('onMouseMove is currently only implemented for jobs not groups');
    if (pageState.selected?.dragging.type === 'pos') {
        const pos = getDragPosMovePos(e, {
            shiftX: pageState.selected.dragging.hoveringPos.shiftX,
            shiftY: pageState.selected.dragging.hoveringPos.shiftY,
        });
        pageState.selected.dragging.hoveringPos.left = pos.left;
        pageState.selected.dragging.hoveringPos.top = pos.top;

        // Check if drag threshold has been exceeded (prevents accidental modifications on click)
        if (!pageState.selected.dragging.dragStarted) {
            const dx = e.clientX - (pageState.selected.dragging.initialMouseX ?? e.clientX);
            const dy = e.clientY - (pageState.selected.dragging.initialMouseY ?? e.clientY);
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < DRAG_THRESHOLD_PX) {
                return; // Not dragging yet, just hovering
            }
            pageState.selected.dragging.dragStarted = true;
        }

        // Calculate drop preview without mutating the job

        if (pageState.selected.item === undefined) return;
        const isSiteVisit = 'visitable_type' in pageState.selected.item;
        pageState.selected.dragging.dropPreview = calculateDropLocation(
            pos.left,
            pos.top,
            pageState.selected.item.duration,
            isSiteVisit,
            dragTargetDiv,
            contentDiv,
            usersWidth,
            rowStepPx,
            view,
            vehicles,
            overlappingJobs,
        );

        // Update employee availability locally during drag using preview times
        const dropPreview = pageState.selected.dragging.dropPreview;
        if (dropPreview === null || dropPreview.start === null || dropPreview.end === null) return;

        recalculateEmployeeAvailability(
            employeesMap,
            pageState.selected.item,
            vehicleAssignmentMap,
            siteVisitMap,
            overlappingEmployeeAssignments,
            dropPreview.start,
            dropPreview.end,
        );
    } else if (pageState.selected.dragging.type === 'rightDur') {
        pageState.selected.dragging.hoveringPos.width = getDragRightDurMove(e, zoom.cellPx, pageState.selected.dragging.startDragDur, 1);
        const durationExactMs = (pageState.selected.dragging.hoveringPos.width / zoom.cellPx) * zoom.zoomRangePreset.cellMs;

        if (pageState.selected.item === undefined) return;
        const roundedEndDate = roundDate(new SvelteDate(pageState.selected.item.start.getTime() + durationExactMs));
        const newDuration = (roundedEndDate.getTime() - pageState.selected.item.start.getTime()) / 1000;

        // Store in dropPreview instead of mutating job directly
        pageState.selected.dragging.dropPreview = {
            start: pageState.selected.item.start,
            end: roundedEndDate,
            vehicle: pageState.selected.item.vehicle,
            duration: newDuration,
        };

        recalculateEmployeeAvailability(
            employeesMap,
            pageState.selected.item,
            vehicleAssignmentMap,
            siteVisitMap,
            overlappingEmployeeAssignments,
            pageState.selected.item.start,
            roundedEndDate,
        );
    } else if (pageState.selected.dragging.type === 'leftDur') {
        const startDateExact = getMouseDate(e.x - pageState.selected.dragging.startDragLeftShift, view.currentZoomStore, contentDiv, usersWidth);
        const startDateRounded = roundDate(startDateExact);
        const durationSec = (pageState.selected.dragging.endDate.getTime() - startDateRounded.getTime()) / 1000;

        const { width, left } = getDragLeftDurMove(e, {
            startDragLeftInitialClientX: pageState.selected.dragging.startDragLeftInitialClientX,
            startDragLeftShift: pageState.selected.dragging.startDragLeftShift,
            cellStepPx: zoom.cellPx,
            startDragLeftDur: pageState.selected.dragging.startDragLeftDur,
        });
        pageState.selected.dragging.hoveringPos.width = width;
        pageState.selected.dragging.hoveringPos.left = left;

        // Store in dropPreview instead of mutating job directly
        const vehicle = vehicles.find((u) => u.id === pageState.selected?.item?.vehicle?.id) ?? null;
        pageState.selected.dragging.dropPreview = {
            start: startDateRounded,
            end: pageState.selected.dragging.endDate,
            vehicle,
            duration: durationSec,
        };

        recalculateEmployeeAvailability(
            employeesMap,
            pageState.selected.item,
            vehicleAssignmentMap,
            siteVisitMap,
            overlappingEmployeeAssignments,
            startDateRounded,
            pageState.selected.dragging.endDate,
        );
    }
};

// Minimum pixels to move before treating as a drag (prevents accidental modifications on click)
const DRAG_THRESHOLD_PX = 4;

export const onMouseDownPosJob = (
    e: MouseEvent,
    boundingRect: DOMRect,
    pageState: PageState<'job'>,
    view: View,
    csrfToken: string,
    baseUrl: string,
    vehicleAssignmentMap: SvelteMap<string, SchedularVehicleAssignment>,
    employeesMap: SvelteMap<string, SchedularEmployee>,
    overlappingEmployeeAssignments: SvelteMap<
        string,
        { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }
    >,
    siteVisitMap?: SiteVisitMap,
) => {
    e.preventDefault();

    pageState.displayPopup = true;
    const pos = getDragPosDownPos(e, boundingRect);
    if (pageState.selected.item === undefined) return;
    // Initialize dropPreview to null - it will be set during mousemove if the user drags past threshold
    pageState.selected.dragging = {
        type: 'pos',
        hoveringPos: {
            left: pos.left,
            top: pos.top,
            width: pageState.selected.item.duration * 1000 * (view.currentZoomStore.cellPx / view.currentZoomStore.zoomRangePreset.cellMs),
            shiftX: pos.shiftX,
            shiftY: pos.shiftY,
        },
        dropPreview: null,
        initialMouseX: e.clientX,
        initialMouseY: e.clientY,
        dragStarted: false,
    };

    if (pageState.selected.item.start === null || pageState.selected.item.end === null) return;
    const vehicleAssignment = pageState.selected.item;
    fetchAvailableEmployees({
        start: pageState.selected.item.start,
        end: pageState.selected.item.end,
        csrfToken,
        baseUrl,
        employeesMap,
        vehicleAssignment,
        overlappingEmployeeAssignments,
        vehicleAssignmentMap,
        siteVisitMap,
    });
};

export const onMouseDownRightDurJob = (
    e: MouseEvent,
    startDragDur: number,
    boundingRect: DOMRect,
    pageState: PageState<'scheduledJob'>,
    view: View,
) => {
    const pos = getDragPosDownPos(e, boundingRect);

    // Initialize dropPreview to null - it will be set during mousemove if the user drags
    pageState.selected.dragging = {
        type: 'rightDur',
        startDragDur,
        hoveringPos: {
            left: pos.left,
            top: pos.top,
            width: pageState.selected.item.duration * 1000 * (view.currentZoomStore.cellPx / view.currentZoomStore.zoomRangePreset.cellMs),
            shiftX: pos.shiftX,
            shiftY: pos.shiftY,
        },
        dropPreview: null,
    };
};

export const onMouseDownLeftDurJob = (e: MouseEvent, boundingRect: DOMRect, pageState: PageState<'scheduledJob'>, view: View) => {
    const { startDragLeftShift, startDragLeftInitialClientX } = getDragLeftDurDown(e, boundingRect);
    const pos = getDragPosDownPos(e, boundingRect);

    const width = pageState.selected.item.duration * 1000 * (view.currentZoomStore.cellPx / view.currentZoomStore.zoomRangePreset.cellMs);

    // Initialize dropPreview to null - it will be set during mousemove if the user drags
    pageState.selected.dragging = {
        type: 'leftDur',
        startDragLeftInitialClientX,
        startDragLeftDur: width,
        startDragLeftShift,
        hoveringPos: {
            left: pos.left,
            top: pos.top,
            width,
            shiftX: pos.shiftX,
            shiftY: pos.shiftY,
        },
        endDate: new SvelteDate(pageState.selected.item.start.getTime() + pageState.selected.item.duration * 1000),
        dropPreview: null,
    };
};

export const onMouseUpJob = (
    e: MouseEvent,
    pageState: PageState<'job'>,
    baseUrl: string,
    csrfToken: string,
    vehicleAssignmentMap: SvelteMap<string, SchedularVehicleAssignment>,
    employeesMap: SvelteMap<string, SchedularEmployee>,
    overlappingEmployeeAssignments: SvelteMap<
        string,
        { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }
    >,
    siteVisitMap?: SiteVisitMap,
) => {
    if (!pageState.selected?.dragging && pageState.selected?.item === undefined) return;

    const vehicleAssignment = pageState.selected?.item;

    if (vehicleAssignment) {
        // if the click was not within the selected job then deselect the job
        const els = document.elementFromPoint(e.x, e.y);
        // els can be null if mouse is released outside the viewport
        if (!els || pageState.selected.item === undefined) {
            return;
        }
        const jobDiv = els.closest(`[data-job-id="${pageState.selected.item.id}"]`);
        //console.log({jobDiv}, pageState.selected.item.id, );
        const dontUnselect = els.closest(`[data-dont-unselect="true"]`);
        if (!dontUnselect && jobDiv === null && !els.classList.contains('loading-wrapper')) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            pageState.selected = null; // TODO: maybe return pageState but with the new type
            pageState.searchStr = '';
        }
    }

    // user was dragging a job somehow
    if (
        pageState?.selected?.dragging?.type === 'pos' ||
        pageState.selected?.dragging?.type === 'rightDur' ||
        pageState.selected?.dragging?.type === 'leftDur'
    ) {
        const dropPreview = pageState.selected.dragging.dropPreview;

        // Apply the drop preview to the actual job
        if (dropPreview && vehicleAssignment) {
            applyDropPreview(vehicleAssignment, dropPreview);

            // Check if the job was dropped outside its date range
            if (
                pageState.selected.dragging.type === 'pos' &&
                dropPreview.start &&
                vehicleAssignment?.job?.start_date_time &&
                vehicleAssignment?.job?.end_date_time
            ) {
                const jobStartDate = new SvelteDate(vehicleAssignment.job.start_date_time);
                const jobEndDate = new SvelteDate(vehicleAssignment.job.end_date_time);
                const assignmentStartDate = new SvelteDate(dropPreview.start);

                // Set hours to 0 for date comparison
                jobStartDate.setHours(0, 0, 0, 0);
                jobEndDate.setHours(0, 0, 0, 0);
                assignmentStartDate.setHours(0, 0, 0, 0);

                if (assignmentStartDate < jobStartDate || assignmentStartDate > jobEndDate) {
                    toast.warning('Vehicle assignment is outside the job date range', {
                        duration: 3000,
                    });
                }
            }
        }

        pageState.selected.dragging = null;
    }

    if (vehicleAssignment === null || vehicleAssignment === undefined || vehicleAssignment.start === null || vehicleAssignment.end !== null) return;

    fetchAvailableEmployees({
        start: vehicleAssignment.start,
        end: vehicleAssignment.end,
        baseUrl,
        csrfToken,
        employeesMap,
        vehicleAssignment,
        overlappingEmployeeAssignments,
        vehicleAssignmentMap,
        siteVisitMap,
    });
};
